import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { DataGrid, GridColDef, GridColumnHeaderParams, GridRowModel } from '@mui/x-data-grid';
import { useGetAllProductCategoriesQuery, useGetAllProductsQuery, useUpdateBatchOfProductsMutation } from '@services';
import { generateUniqueKey, getZoneId } from '@helpers';
import { CustomField, JimboResponse, Product, ProductCategory } from '@models';
import { Autocomplete, Chip, debounce, Grid, TextField, Tooltip, Box, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useAppSelector } from '@hooks/redux-custom';
import { LoadingSpinner } from '@components';
import { showToast } from '@store/slices/notification';
import { useAppDispatch } from '@hooks/redux-custom';
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed';
import { LoadingButton } from '@mui/lab';
import { changeToNewRanking, ProductItem, findChangedProductsList } from './ProductSortList.service';

type ProductRankingTable = {
  id: number;
  sku: string;
  name: string;
  brand?: string;
  categories: string[];
  sort_order: number;
  custom_fields?: CustomField[];
}[];

export const ProductSortList: FC = () => {
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');
  const [rows, setRows] = useState<ProductRankingTable>([]);
  const [localProducts, setLocalProducts] = useState<Product[]>([]);
  const [isOrderChanged, setIsOrderChanged] = useState<boolean>(false);
  const [isBatchUpdateProductRankProcessing, setIsBatchUpdateProductRankProcessing] = useState<boolean>(false);
  const [updateCount, setUpdateCount] = useState(0);

  const [updateProducts] = useUpdateBatchOfProductsMutation();
  const dispatch = useAppDispatch();

  const { data: products, isLoading } = useGetAllProductsQuery();
  const { data: categories } = useGetAllProductCategoriesQuery();

  const activeUser = useAppSelector((state) => state.activeUser.value);
  const userAccessStatus = activeUser?.user?.roles.find((role: any) => role.moduleName === 'product');
  const hasWriteAccess = userAccessStatus.role > 1;

  const zoneId = getZoneId(categories);

  const getCategories = useCallback(
    (product: Product) =>
      product.categories.reduce((productCategories, id) => {
        const category = categories?.find((c) => c.id === id && c.parent_id !== zoneId);
        return category ? [...productCategories, category] : productCategories;
      }, [] as ProductCategory[]),
    [categories, zoneId],
  );

  const getCategoryNameById = useCallback(
    (categoryId: number) => {
      const category = categories?.find((c) => {
        return c.id === categoryId;
      });

      if (category) {
        return category.name;
      } else {
        return 'No Category';
      }
    },
    [categories],
  );

  const uniqueCategories = useMemo(() => {
    const filteredCategories = categories?.filter((category) => {
      return category.parent_id !== zoneId && category.id !== zoneId && category.id !== 40;
    });

    if (filteredCategories) {
      const categorySet: string[] = [];

      filteredCategories.forEach((category) => categorySet.push(category.name));

      return categorySet;
    }
  }, [categories, zoneId]);

  const columns: GridColDef[] = [
    {
      field: 'sku',
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="body1" sx={{ fontSize: 18, fontWeight: 600 }}>
          {'Sku'}
        </Typography>
      ),
      flex: 0.5,
    },
    {
      field: 'name',
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="body1" sx={{ fontSize: 18, fontWeight: 600 }}>
          {'Name'}
        </Typography>
      ),
      flex: 2,
      renderCell: (it) => {
        const { value, row } = it;
        const isBundleIndex = row.custom_fields.findIndex((field: { name: string }) => field.name === 'is_bundle');
        const isBundle = row?.custom_fields[isBundleIndex]?.value;
        return (
          <Tooltip title={value}>
            <Box display={'flex'} justifyContent={'center'}>
              {isBundleIndex > -1 && isBundle === 'true' && <DynamicFeedIcon sx={{ marginRight: 1 }} />}
              <span>{value}</span>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'brand',
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="body1" sx={{ fontSize: 18, fontWeight: 600 }}>
          {'Brand'}
        </Typography>
      ),
      flex: 1,
      renderCell: (params: any) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: 'categories',
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="body1" sx={{ fontSize: 18, fontWeight: 600 }}>
          {'Categories'}
        </Typography>
      ),
      flex: 2,
      renderCell: ({ value }) =>
        value?.map((categoryName: string) => (
          <Tooltip title={value.join(', ')} key={generateUniqueKey()}>
            <span>
              <Chip label={categoryName} size="small" sx={{ mr: 1 }} />
            </span>
          </Tooltip>
        )),
    },
    {
      field: 'sort_order',
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="body1" sx={{ fontSize: 18, fontWeight: 600 }}>
          {'Rank'}
        </Typography>
      ),
      flex: 1,
      editable: hasWriteAccess,
    },
  ];

  const handleSearchTextChangeDebounced = useMemo(() => debounce((text: string) => setSearchText(text), 300), []);

  const processRowUpdate = useCallback(
    // processRowUpdate must be async callback, it doesn't return updated row directly
    async (newRow: GridRowModel, oldRow: GridRowModel) => {
      return new Promise((resolve, reject) => {
        // creates a deep clone of the products array
        const newProducts = JSON.parse(JSON.stringify(localProducts));

        if (isNaN(newRow.sort_order)) {
          newRow = oldRow;
        } else {
          newRow.sort_order = parseInt(newRow.sort_order);

          const changedProducts = changeToNewRanking(
            newProducts as unknown as ProductItem[],
            oldRow.id,
            Number(newRow.sort_order),
          ) as unknown as ProductRankingTable;

          setIsOrderChanged(true);
          setLocalProducts(changedProducts as unknown as Product[]);
        }

        resolve(newRow);
      });
    },
    [localProducts],
  );

  const handleProcessRowUpdateError = (error: Error) => {
    console.log(error);
  };

  const loopBatchProductRankingUpdate = useCallback(
    (count = 0) => {
      const changedProducts = findChangedProductsList(products as unknown as ProductItem[], localProducts as unknown as ProductItem[]);

      if (count >= changedProducts.length) {
        setUpdateCount(0);

        dispatch(
          showToast({
            message: 'Product Rankings successfully updated!',
            type: 'success',
          }),
        );

        setIsBatchUpdateProductRankProcessing(false);
        setIsOrderChanged(false);
        return;
      }

      setIsBatchUpdateProductRankProcessing(true);

      // If more then 10 product update ranks in array, get the first 10
      const productToUpdate = changedProducts.slice(count, count + 10);

      updateProducts(productToUpdate)
        .unwrap()
        .then(() => {
          // get the next 10 product ranks to update in the array, updates the count state which triggers the function loop again
          setUpdateCount((count) => count + 10);
        })
        .catch((error: { data: JimboResponse }) => {
          console.log(error);

          dispatch(
            showToast({
              message: 'Failed to update product rankings!',
              type: 'error',
            }),
          );

          setUpdateCount(0);
          setIsBatchUpdateProductRankProcessing(false);
        });
    },
    [products, localProducts, updateProducts as unknown, dispatch],
  );

  useEffect(() => {
    if (products) {
      setLocalProducts(products);
    }
  }, [products]);

  useEffect(() => {
    if (localProducts) {
      const productsRow = localProducts?.reduce((localProducts, product) => {
        const categories = getCategories(product).map(({ id }) => getCategoryNameById(id));

        if (!selectedCategories.length || selectedCategories.find((value) => categories.includes(value))) {
          localProducts.push({
            id: product.id,
            sku: product.sku,
            name: product.name,
            brand: product.custom_fields?.find((field) => field.name === 'brand')?.value,
            categories,
            sort_order: product.sort_order,
            custom_fields: product.custom_fields,
          });
        }

        return localProducts;
      }, [] as ProductRankingTable);

      setRows(productsRow);
    }
  }, [localProducts, categories, selectedCategories, searchText, getCategories, getCategoryNameById]);

  useEffect(() => {
    // State change will continuously call the loop update product rank
    if (updateCount) loopBatchProductRankingUpdate(updateCount);
  }, [updateCount]);

  return isLoading ? (
    <LoadingSpinner isOpen={isLoading} />
  ) : (
    <Grid data-testid="product-sort-grid" container spacing={2} sx={{ py: 2 }}>
      <Grid item xs={4}>
        <Autocomplete
          options={uniqueCategories || []}
          getOptionLabel={(option: string) => option}
          renderInput={(params) => <TextField {...params} label="Filter by categories" />}
          multiple
          filterSelectedOptions
          onChange={(e, categories) => {
            setSelectedCategories(categories);
          }}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          placeholder="Search Product"
          fullWidth
          onChange={(e) => handleSearchTextChangeDebounced(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" />,
          }}
        />
      </Grid>
      <Grid item xs={2}>
        <LoadingButton
          loading={isBatchUpdateProductRankProcessing}
          variant="contained"
          disabled={!isOrderChanged}
          onClick={() => {
            loopBatchProductRankingUpdate();
          }}
        >
          Save Product Ranking
        </LoadingButton>
      </Grid>
      <Box sx={{ height: 800, width: '100%', marginTop: 2 }}>
        {!!rows && (
          <DataGrid
            initialState={{
              sorting: {
                sortModel: [{ field: 'sort_order', sort: 'asc' }],
              },
            }}
            rows={rows}
            columns={columns}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={handleProcessRowUpdateError}
            experimentalFeatures={{ newEditingApi: true }}
            filterModel={{ items: [{ columnField: 'name', operatorValue: 'contains', value: searchText }] }}
            sx={{
              '.MuiDataGrid-columnSeparator': {
                display: 'none',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#CD9BF0',
                color: '#161616',
              },
              '& .MuiDataGrid-row': {
                '&:hover': {
                  backgroundColor: '#faf2ff',
                },
              },
            }}
          />
        )}
      </Box>
    </Grid>
  );
};
